/**
 * Frontend Permissions Utility
 * TypeScript version of backend permissions logic
 * Determines module access based on user role + subscription plan
 */

// Import database permissions
import { getCachedScreenPermissions } from './databasePermissions';

// User Role Types
export const USER_ROLES = {
  ADMIN: 'Admin',                           // Brokerage owner / main account holder
  AGENT_UNDER_BROKER: 'AgentUnderBroker',   // Agent invited by broker (Essential features)
  AGENT_UNDER_BROKER_AI: 'AgentUnderBrokerAI', // Agent invited by broker (AI features)
  INTERNAL_EMPLOYEE: 'InternalEmployee',    // KairosIQ team member
  REGULAR_USER: 'RegularUser'               // Individual subscriber
} as const;

// Subscription Plans
export const SUBSCRIPTION_PLANS = {
  ESSENTIAL: 'Essential',                   // $50/month - Basic CRM
  PROFESSIONAL: 'Professional',             // $250/month - AI Lead Coach
  DEAL_AI: 'DealAI',                        // $350/month - Customer AI Assistants
  AGENT_K: 'AgentK',                        // $500/month - Premium 6-AI Agent System
  BROKER_PLAN: 'BrokerPlan',                // $1000/month - Broker features
  AGENT_UNDER_BROKER: 'AgentUnderBroker',   // $50/month - Agent under broker (Essential)
  AGENT_UNDER_BROKER_AI: 'AgentUnderBrokerAI', // $200/month - Agent under broker (AI, 20% discount)
  INTERNAL_EMPLOYEE: 'InternalEmployee',    // $0/month - KairosIQ internal team
  FREE_TRIAL: 'FreeTrial'                   // Free trial period
} as const;

export interface User {
  user_role?: string;
  subscription_plan?: string;
  is_admin?: boolean | number;
}

/**
 * Module Access Rules - DEPRECATED
 * Now using database-driven permissions
 * These are kept only as absolute fallbacks
 */
export const MODULE_ACCESS_RULES: Record<string, (user: User) => boolean> = {
  // Basic modules - generally available
  dashboard: () => true,
  leads: () => true,
  deals: () => true,
  collaborators: () => true,
  companyProfile: () => true,
  chat: () => true,
  settings: () => true,
  investments: (user) => isInternalEmployee(user),  // Investor Deals - internal employees only

  // All other modules default to database permissions
  analytics: () => false,  // Database controlled
  marketIntelligence: () => false,  // Database controlled
  cmaSmartWorkflow: () => false,  // Database controlled
  brokerIntelligence: () => false,  // Database controlled
  // brokerPortal — now defined below with proper role checks (KOPS-468)
  mlsPortal: () => false,  // Database controlled
  aiOperations: () => false,  // Database controlled
  facebookAdsManager: () => false,  // Database controlled — KOPS-671

  // ──────────────────────────────────────────────────────────────────────
  // KOPS-465 / KOPS-511 — Simplified plan/role gates (no screen_registry).
  // These rules are the production gate: they stay intact at migration
  // swap day and are the pattern to copy for all future admin surfaces.
  // ──────────────────────────────────────────────────────────────────────

  /**
   * Broker Intelligence Hub V2 — admin/oversight hub.
   * Access granted to:
   *   - Broker-role users (user_role: BROKER, broker_admin, admin)
   *   - Agents with brokerage admin role (member_role='admin' or is_brokerage_admin)
   *   - Internal KairosIQ employees
   */
  brokerIntelligenceV2: (user: any) => {
    if (isInternalEmployeeFlex(user)) return true;
    if (isBrokerOrAdmin(user)) return true;
    if (hasBrokerPlan(user)) return true;
    if (isBrokerageAdmin(user)) return true;
    return false;
  },

  /**
   * Broker Portal — operational workflow hub.
   * Same access rules as Broker Intelligence — brokers, brokerage admins, internal.
   */
  brokerPortal: (user: any) => {
    if (isInternalEmployeeFlex(user)) return true;
    if (isBrokerOrAdmin(user)) return true;
    if (hasBrokerPlan(user)) return true;
    if (isBrokerageAdmin(user)) return true;
    return false;
  },

  /**
   * Agent Portal — compliance + document submission.
   * Access granted to:
   *   - All agents under a broker (free compliance tier)
   *   - Direct subscribers (agent-hub / ai-search / ai-pro)
   *   - Brokers (they can see agent view too)
   *   - Internal employees
   */
  agentPortal: (user: any) => {
    if (isInternalEmployeeFlex(user)) return true;
    if (isBrokerOrAdmin(user)) return true;
    if (hasBrokerPlan(user)) return true;
    if (hasAgentTierPlan(user)) return true;
    if (isAgentRole(user)) return true;
    // Any user with a brokerage membership gets agent portal (free compliance)
    if (hasBrokerageMembership(user)) return true;
    return false;
  },
};

// ──────────────────────────────────────────────────────────────────────
// Shared helpers — normalize across the two user shapes that exist in
// the codebase: /auth/me returns {role, subscription, paymentStatus,
// permissions, brokerage_id}, legacy type uses {user_role, subscription_plan, is_admin}.
// ──────────────────────────────────────────────────────────────────────

function fieldRole(user: any): string {
  return (user?.role || user?.user_role || '').toString();
}
function fieldSubscription(user: any): string {
  return (user?.subscription || user?.subscription_plan || user?.planId || '').toString();
}

export function isBrokerOrAdmin(user: any): boolean {
  const role = fieldRole(user);
  return role === 'broker' || role === 'broker_admin' || role === 'admin' || role === 'Admin';
}

export function isInternalEmployeeFlex(user: any): boolean {
  const role = fieldRole(user).toLowerCase();
  const subscription = fieldSubscription(user);
  const paymentStatus = (user?.paymentStatus || user?.payment_status || '').toString();
  const permissions: string[] = Array.isArray(user?.permissions) ? user.permissions : [];

  // Subscription package name
  if (subscription === 'InternalEmployee') return true;
  if (paymentStatus === 'NoNeedToPay') return true;

  // Role detection — matches AgentDashboardModern's working JWT-decode pattern
  if (role === 'internalemployee' || role === 'internal_employee') return true;
  if (role === 'manager') return true;

  // Permission-derived
  if (permissions.includes('internal_tools') || permissions.includes('full_access')) return true;

  return false;
}

export function hasBrokerPlan(user: any): boolean {
  const sub = fieldSubscription(user);
  return sub === 'broker' || sub === 'Broker' || sub === 'BrokerPlan';
}

export function hasAgentTierPlan(user: any): boolean {
  const sub = fieldSubscription(user);
  return sub === 'agent-hub' || sub === 'ai-search' || sub === 'ai-pro';
}

export function isAgentRole(user: any): boolean {
  const role = fieldRole(user);
  return role === 'agent' || role === 'real_estate_agent' || role === 'REAL_ESTATE_AGENT' ||
    role === 'AgentUnderBroker' || role === 'AgentUnderBrokerAI';
}

/**
 * Check if user is a brokerage admin (agent with admin privileges).
 * Set by /auth/me → member_role='admin' or is_brokerage_admin=true.
 * Grants access to Broker Intelligence + Broker Portal.
 */
export function isBrokerageAdmin(user: any): boolean {
  if (user?.is_brokerage_admin) return true;
  if (user?.member_role === 'admin') return true;
  return false;
}

/**
 * Check if user has any brokerage membership (even as regular agent).
 * Grants free compliance-tier Agent Portal access.
 */
export function hasBrokerageMembership(user: any): boolean {
  return !!user?.brokerage_id || !!user?.member_role;
}

/**
 * Check if a user has permission to access a module
 */
export const hasModuleAccess = (moduleKey: string, user?: User): boolean => {
  if (!user) return false;

  // Check database permissions FIRST - this is now the primary source
  const dbPermissions = getCachedScreenPermissions();
  
  // If we have database permissions loaded, use them
  if (Object.keys(dbPermissions).length > 0) {
    if (moduleKey in dbPermissions) {
      const accessLevel = dbPermissions[moduleKey];
      const hasAccess = accessLevel === 'full' || accessLevel === 'read';
      
      console.log(`📋 [Permissions] ${moduleKey}: ${accessLevel} (database) - ${hasAccess ? 'ALLOWED' : 'DENIED'}`);
      return hasAccess;
    }
  }

  // Only fall back to hardcoded rules if database permissions aren't loaded
  // This should rarely happen after initialization
  console.warn(`⚠️ [Permissions] No database permission for ${moduleKey}, using fallback rules`);
  
  const accessRule = MODULE_ACCESS_RULES[moduleKey];
  if (!accessRule) return false;

  try {
    return accessRule(user);
  } catch (error) {
    console.error(`Error checking access for module ${moduleKey}:`, error);
    return false;
  }
};

/**
 * Get all accessible modules for a user
 */
export const getUserAccessibleModules = (user?: User): string[] => {
  if (!user) return [];

  const accessibleModules: string[] = [];

  for (const [moduleKey, accessRule] of Object.entries(MODULE_ACCESS_RULES)) {
    if (hasModuleAccess(moduleKey, user)) {
      accessibleModules.push(moduleKey);
    }
  }

  return accessibleModules;
};

/**
 * Helper Functions
 */

export const isBroker = (user?: User): boolean => {
  return user?.user_role === USER_ROLES.ADMIN &&
         user?.subscription_plan === SUBSCRIPTION_PLANS.BROKER_PLAN;
};

export const isAgentUnderBroker = (user?: User): boolean => {
  return user?.user_role === USER_ROLES.AGENT_UNDER_BROKER ||
         user?.user_role === USER_ROLES.AGENT_UNDER_BROKER_AI;
};

export const isInternalEmployee = (user?: User): boolean => {
  return user?.user_role === USER_ROLES.INTERNAL_EMPLOYEE;
};

export const hasAIFeatures = (user?: User): boolean => {
  const aiPlans = [
    SUBSCRIPTION_PLANS.PROFESSIONAL,
    SUBSCRIPTION_PLANS.DEAL_AI,
    SUBSCRIPTION_PLANS.AGENT_K,
    SUBSCRIPTION_PLANS.AGENT_UNDER_BROKER_AI,
    SUBSCRIPTION_PLANS.BROKER_PLAN
  ];
  return aiPlans.includes(user?.subscription_plan as any);
};

export const getSubscriptionTier = (user?: User): number => {
  const tiers: Record<string, number> = {
    [SUBSCRIPTION_PLANS.ESSENTIAL]: 1,
    [SUBSCRIPTION_PLANS.AGENT_UNDER_BROKER]: 1,
    [SUBSCRIPTION_PLANS.PROFESSIONAL]: 2,
    [SUBSCRIPTION_PLANS.AGENT_UNDER_BROKER_AI]: 2,
    [SUBSCRIPTION_PLANS.DEAL_AI]: 3,
    [SUBSCRIPTION_PLANS.AGENT_K]: 4,
    [SUBSCRIPTION_PLANS.BROKER_PLAN]: 5,
    [SUBSCRIPTION_PLANS.INTERNAL_EMPLOYEE]: 99,
    [SUBSCRIPTION_PLANS.FREE_TRIAL]: 0
  };
  return tiers[user?.subscription_plan || ''] || 0;
};
